import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import { openCookieSettings } from '../utils/consent';
import logo from './classlabs.png';
import sensibleSencoLogo from './SENsible-Senco_Round-Logo_white-1-removebg-preview.png';

const FEATURE_CHIPS = [
  { icon: '🎚️', label: 'Dynamic difficulty' },
  { icon: '🔊', label: 'Read aloud' },
  { icon: '📖', label: 'Reading-age matched' },
  { icon: '🎨', label: 'Colour overlays' },
  { icon: '🔤', label: 'Dyslexia-friendly font' },
  { icon: '🌍', label: 'Home-language support' },
];

const SensibleSencoReview = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="cl-page">
      <div className="cl-pattern-bg" />

      {/* Nav */}
      <nav className="cl-nav">
        <div className="cl-nav-inner">
          <div className="cl-nav-spacer">
            <button
              type="button"
              className="cl-nav-burger"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span /><span /><span />
            </button>
          </div>
          <div className="cl-nav-logo-group">
            <Link to="/">
              <img src={logo} alt="ClassLabs" className="cl-logo-main" />
            </Link>
          </div>
          <div className="cl-nav-actions">
            <Link to="/login" className="cl-nav-signin">Log In</Link>
            <Link to="/register-interest" className="cl-nav-cta">Book Demo</Link>
          </div>
        </div>
      </nav>

      {/* Slide-in side nav */}
      <div
        className={`cl-drawer-backdrop ${menuOpen ? 'is-open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />
      <aside className={`cl-drawer ${menuOpen ? 'is-open' : ''}`} aria-hidden={!menuOpen}>
        <button
          type="button"
          className="cl-drawer-close"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        >
          ×
        </button>
        <nav className="cl-drawer-links">
          <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
          <a href="/#offers" onClick={() => setMenuOpen(false)}>How it works</a>
          <a href="/#testimonials" onClick={() => setMenuOpen(false)}>What schools say</a>
          <Link to="/sensiblesencoreview" onClick={() => setMenuOpen(false)}>SENDCO review</Link>
        </nav>
        <div className="cl-drawer-actions">
          <Link to="/login" className="cl-drawer-signin" onClick={() => setMenuOpen(false)}>Log In</Link>
          <Link to="/register-interest" className="cl-drawer-cta" onClick={() => setMenuOpen(false)}>Book Demo</Link>
        </div>
      </aside>

      {/* Review hero */}
      <div className="cl-wrap">
        <header className="cl-rvw-hero">
          <div className="cl-eyebrow">Product Review</div>
          <div className="cl-rvw-credit">
            <h1 className="cl-rvw-title">
              Reviewed by <span className="cl-h2-accent">Abigail Hawkins</span>, SENDCO Solutions
            </h1>
            <img src={sensibleSencoLogo} alt="SENsible SENCO" className="cl-rvw-credit-logo" />
          </div>
        </header>
      </div>

      {/* Video */}
      <section className="cl-section" style={{ paddingTop: 24 }}>
        <div className="cl-wrap">
          <div className="cl-video-frame">
            <div className="cl-video-embed">
              <iframe
                src="https://player.vimeo.com/video/1211342568?h=4a42670a45&dnt=1"
                title="ClassLabs — reviewed by the experts"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Review body */}
      <section className="cl-section" style={{ paddingTop: 8 }}>
        <div className="cl-wrap">
          <article className="cl-rvw-body">
            <p>
              For class teachers managing the daily reality of a mixed-needs classroom, the gap
              between “this pupil needs adapted content” and “here is the adapted content” is largely
              filled with <span className="cl-emph">unpaid time</span>. ClassLabs is built to close that gap.
            </p>
            <p>
              The platform allows teachers to upload their own lesson materials (PDFs, Word
              documents, PowerPoints) or draw on a library of <span className="cl-emph">over 4,400 Oak National Academy
              resources</span> aligned to the 2014 National Curriculum from Year 1 to Year 11. The AI
              identifies learning objectives and Tier 3 vocabulary from the uploaded content, then
              generates <span className="cl-emph">individually tailored tutorials</span> for each pupil based on their reading age, SEN
              profile, and home language. Pupil profiles are created once and reused across every
              lesson. The setup overhead is genuinely front-loaded: content uploads take around five
              minutes, after which tutorials are generated rapidly, though where the EAL translation
              feature is used this can take significantly longer.
            </p>

            <div className="cl-rvw-sec">
              <div className="cl-rvw-sec-head">
                <span className="cl-rvw-sec-icon" style={{ background: 'var(--teal)' }}>🎯</span>
                <h2 className="cl-h2">Adaptive by Design</h2>
              </div>
              <p>
                What makes ClassLabs more than a standard differentiation tool is the adaptive layer
                built into each tutorial. Multiple choice questions <span className="cl-emph">adjust dynamically in real time</span>, getting
                harder if a pupil is doing well and easier if they are struggling. For pupils who find
                traditional worksheets defeating, this matters. The platform can <span className="cl-emph">keep them engaged</span> at
                exactly the point where a paper task would have lost them, and it does so without
                drawing attention to the adjustment.
              </p>
              <p>
                Tutorial content is broken into smaller segments to support access, and the structure
                includes narrated slides, Tier 3 vocabulary matching, and interactive questions with
                instant feedback. Audio narration is available at three adjustable speeds. Font choice
                and background colour can be <span className="cl-emph">personalised per pupil</span>; options include OpenDyslexic,
                Arial, and Nunito as default, with cream, blue, or peach backgrounds. For a child who
                needs one combination and a classmate who needs another, these are set once and
                then applied automatically.
              </p>
              <div className="cl-rvw-chips">
                {FEATURE_CHIPS.map((f) => (
                  <span className="cl-rvw-chip" key={f.label}>
                    <span className="cl-rvw-chip-icon" aria-hidden="true">{f.icon}</span>
                    {f.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="cl-rvw-sec">
              <div className="cl-rvw-sec-head">
                <span className="cl-rvw-sec-icon" style={{ background: 'var(--coral)' }}>📊</span>
                <h2 className="cl-h2">What It Means for SENCOs</h2>
              </div>
              <p>
                Each completed tutorial generates a report detailing which scaffolding strategies were
                used, the research principles behind them, and how the pupil performed, including time
                on task, question-level accuracy, and engagement patterns. Real-time analytics are also
                available at class level, enabling teachers to see which pupils need more support with
                specific content without waiting for formal assessment.
              </p>
              <p>
                That <span className="cl-emph">evidence trail</span> has value well beyond the individual lesson. It supports APDR cycles,
                annual reviews, ISPs, and conversations with external professionals: educational
                psychologists, speech and language therapists, and others who need to understand how
                a pupil accesses learning. A NNoW/JCQ dashboard, designed in consultation with Lia
                Castiglione, brings additional tracking functionality to support formal assessment and
                accountability requirements.
              </p>
            </div>

            <div className="cl-rvw-sec">
              <div className="cl-rvw-sec-head">
                <span className="cl-rvw-sec-icon" style={{ background: 'var(--teal)' }}>🤝</span>
                <h2 className="cl-h2">Who It Is For</h2>
              </div>
              <p>
                ClassLabs is currently best suited to <span className="cl-emph">Key Stage 2</span>, where the balance of digital
                independence and content complexity sits well with what the platform delivers. It works
                across all subjects with the exception of maths, where the delivery mechanism requires
                a different approach. A Supply Teacher version is already in production, creating the
                same adaptive tutorials with anonymous pupil profiles. This version is currently being
                used in schools with positive feedback and extends the platform’s reach to supply staff
                managing unfamiliar classes.
              </p>
            </div>

            <div className="cl-rvw-verdict">
              <div className="cl-rvw-verdict-head">
                <span className="cl-rvw-stars" aria-hidden="true">★★★★★</span>
                <h2 className="cl-h2">Verdict</h2>
              </div>
              <p>
                ClassLabs is a practical, thoughtfully designed platform with genuine application for
                mainstream settings. The <span className="cl-emph">time-saving case is real</span>, and teachers who have used it report
                that it delivers. The evidence trail it generates adds a layer of value that extends well
                beyond the classroom, feeding into reviews, reports, and professional conversations
                that SENCOs are already having. It is <span className="cl-emph">worth a serious look</span>.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="cl-section cl-cta-final" id="cta">
        <div className="cl-wrap">
          <div className="cl-eyebrow" style={{ marginBottom: 24 }}>See it in action</div>
          <h2 className="cl-h2">Every tutorial as <span className="cl-cta-accent">unique as the student</span></h2>
          <p className="sub">Join the schools already using ClassLabs to adapt learning for every pupil. Leave your details, and we'll arrange a personalised demo.</p>
          <Link to="/register-interest" className="cl-btn-final">Book a demo</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="cl-footer">
        <div className="cl-wrap">
          <div className="cl-footer-grid">
            <div className="cl-footer-brand">
              <img src={logo} alt="ClassLabs" className="cl-footer-logo" />
              <p>The Digital Teaching Assistant Trusted by UK SEND Units — adapting your content to every learner, backed by evidence and built for impact.</p>
              <div className="cl-footer-socials">
                <a href="mailto:info@classlabs.co.uk" className="cl-footer-social" aria-label="Email ClassLabs">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/classlabsuk/" target="_blank" rel="noopener noreferrer" className="cl-footer-social" aria-label="ClassLabs on LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="cl-footer-col">
              <h4>Quick Links</h4>
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/register-interest">Book a demo</Link></li>
                <li><Link to="/login">Log in</Link></li>
              </ul>
            </div>
            <div className="cl-footer-col">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><a href="mailto:info@classlabs.co.uk">Contact Us</a></li>
                <li><button type="button" className="cl-footer-cookie-btn" onClick={openCookieSettings}>Cookie settings</button></li>
              </ul>
            </div>
          </div>
          <div className="cl-footer-legal">
            <p>© 2026 ClassCompass Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SensibleSencoReview;
